import React, {
    useCallback,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import SliderPattern from '@mlb/react-patterns/components/Slider/slider.tsx';
import {
    Rail,
    RailHotspot,
    Dot,
    Track,
} from '@mlb/react-patterns/components/Slider/slider.style.ts';
import COLORS from '@mlb/react-patterns/style/universal.color';

import { Tracking } from 'bdvp-tracking';

import { segmentedMode } from '../utils';

const Slider = styled(SliderPattern)`
    user-select: none;

    ${Rail} {
        width: calc(100% + 20px);
        margin: 0 -10px;
        background-color: ${COLORS.mlbGrey5};
    }

    ${Track} {
        background-color: ${COLORS.liveYellow};
    }

    ${RailHotspot} {
        height: 30px;
        transform: translate(0%,0%);
    }
`;

const Trimmer = (props) => {

    const {
        endTime,
        getRoundedSegment,
        isIos,
        wasPlaying,
        isShowingTicks,
        isSliding,
        isTrimming,
        mediaPlayer,
        playhead,
        segments,
        setEndTime,
        setPlayhead,
        setStartTime,
        step,
        startTime,
    } = props;

    const tickValues = useMemo(() => [...new Set(segments)], [segments]);

    const mode = useCallback((curr, next) => {

        return segmentedMode(curr, next, segments, getRoundedSegment);

    }, [
        segments,
        getRoundedSegment,
    ]);

    const keyDownHandler = useCallback((e, activeHandleId) => {

        const validUpKeys = ['ArrowRight'];
        const validDownKeys = ['ArrowLeft'];
        const startHandleId = '$$-0';
        const endHandleId = '$$-1';

        const key = e.key || `${e.keyCode}`;

        if (!activeHandleId || !validUpKeys.concat(validDownKeys).includes(key)) {

            return false;

        }

        e.stopPropagation && e.stopPropagation();
        e.preventDefault && e.preventDefault();

        if (validUpKeys.includes(key)) {

            if (activeHandleId === startHandleId) {

                const newStartTime = segments[segments.indexOf(startTime) + 1];

                if (newStartTime >= endTime) {

                    return;

                }

                if (newStartTime > playhead) {

                    setPlayhead(newStartTime);

                }

                setStartTime(newStartTime);

            }

            if (activeHandleId === endHandleId) {

                const newEndTime = segments[segments.indexOf(endTime) + 1];

                if (typeof newEndTime === 'undefined') {

                    return;

                }

                setEndTime(newEndTime);

            }


        } else if (validDownKeys.includes(key)) {

            if (activeHandleId === startHandleId) {

                const newStartTime = segments[segments.indexOf(startTime) - 1];

                if (typeof newStartTime === 'undefined') {

                    return;

                }

                setStartTime(newStartTime);

            }

            if (activeHandleId === endHandleId) {

                const newEndTime = segments[segments.indexOf(endTime) - 1];

                if (newEndTime <= startTime) {

                    return;

                }

                if (newEndTime <= playhead) {

                    setPlayhead(startTime);

                }

                setEndTime(newEndTime);

            }

        }

    }, [
        segments,
        setStartTime,
        setEndTime,
        startTime,
        endTime,
        playhead,
        setPlayhead,
    ]);

    return (
        <Slider
            defaultValues={[
                startTime,
                endTime,
            ]}
            minValue={0}
            maxValue={segments[segments.length - 1]}
            handlers={{
                onSlideStart: () => {

                    isSliding.current = true;
                    isTrimming.current = true;

                    if (wasPlaying) {

                        mediaPlayer.pause();

                    }

                },
                onSlideEnd: () => {

                    Tracking.trackReelCreationSettingInOutPoints();

                    isSliding.current = false;
                    isTrimming.current = false;

                    if (wasPlaying) {

                        mediaPlayer.play();

                    }

                },
                onUpdate: (values) => {

                    // Given that most decimal fractions cannot be represented exactly in binary
                    // And the values coming from react-compound-slider currently do not enforce fixed decimal places matching the steps
                    // We want to ensure start and end times added to state have been "fixed" (rounded) to match rounded segments
                    const nextStartTime = getRoundedSegment(values[0]);
                    const nextEndTime = getRoundedSegment(values[1]);

                    if (
                        startTime === nextStartTime
                        && endTime === nextEndTime
                    ) {

                        return false;

                    }

                    if (startTime !== nextStartTime) {

                        setStartTime(nextStartTime);

                    }

                    if (endTime !== nextEndTime) {

                        setEndTime(nextEndTime);

                    }

                    if (
                        isSliding.current
                        && (
                            playhead <= nextStartTime
                            || playhead >= nextEndTime
                        )
                    ) {

                        if ((!isIos || wasPlaying) && nextStartTime !== playhead) {

                            mediaPlayer.seek(nextStartTime);

                        }

                        setPlayhead(nextStartTime);

                    }


                },
            }}
            step={step}
            mode={mode}
            renderRail={({ getRailProps }) => {

                return (
                    <>
                        <Rail
                            theme={'dark'}
                        />
                        <RailHotspot
                            {...getRailProps({
                                onMouseDown: () => isSliding.current = true,
                                onTouchStart: () => isSliding.current = true,
                            })}
                        />
                    </>
                );

            }}
            renderHandles={({
                handles,
                getHandleProps,
                activeHandleID,
            }) => {

                return (
                    <>
                        {handles.map(handle => (
                            <Dot
                                key={handle.id}
                                left={`${handle.percent}%`}
                                theme={'dark'}
                                role={'slider'}
                                aria-valuemin={0}
                                aria-valuemax={segments[segments.length - 1]}
                                aria-valuenow={handle.value}
                                isActive={activeHandleID === handle.id}
                                {...getHandleProps(handle.id)}
                                onKeyDown={(e) => keyDownHandler(e, handle.id)}
                            />
                        ))}
                    </>
                );

            }}
            {...(isShowingTicks && { tickValues })}
        />
    );

};

Trimmer.propTypes = {
    endTime: PropTypes.number.isRequired,
    getRoundedSegment: PropTypes.func.isRequired,
    wasPlaying: PropTypes.bool.isRequired,
    isSliding: PropTypes.shape({
        current: PropTypes.bool.isRequired,
    }).isRequired,
    isTrimming: PropTypes.shape({
        current: PropTypes.bool.isRequired,
    }).isRequired,
    mediaPlayer: PropTypes.object.isRequired,
    playhead: PropTypes.number.isRequired,
    segments: PropTypes.array.isRequired,
    setEndTime: PropTypes.func.isRequired,
    setPlayhead: PropTypes.func.isRequired,
    setStartTime: PropTypes.func.isRequired,
    step: PropTypes.number.isRequired,
    startTime: PropTypes.number.isRequired,
    isIos: PropTypes.bool,
    isShowingTicks: PropTypes.bool,
};

Trimmer.defaultProps = {
    isIos: false,
    isShowingTicks: false,
};

export default Trimmer;
